import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, useWatch } from 'react-hook-form'
import type { Path } from 'react-hook-form'
import { z } from 'zod'
import { Button, PasswordField } from '@shared/design-system/ui'
import { useSession } from '@shared/lib/auth/session-store'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { toast } from '@shared/lib/toast/toast-store'
import { applyApiFieldErrors } from '@shared/lib/forms/api-field-errors'
import { ResetPasswordInputSchema } from '../api/auth.contract'
import { useResetPasswordMutation } from '../api/auth.mutations'
import { getPasswordStrength } from '../model/password-strength'
import { getSearchToken, navigateTo } from './auth-routing'
import { actions, form, link } from './auth.css'
import { AuthLayout } from './AuthLayout'

const ResetFormSchema = z
  .object({
    password: ResetPasswordInputSchema.shape.password,
    confirmPassword: z.string().min(1, 'Confirme a senha.'),
  })
  .refine((value) => value.password === value.confirmPassword, {
    message: 'As senhas precisam ser iguais.',
    path: ['confirmPassword'],
  })

type ResetForm = z.infer<typeof ResetFormSchema>

type ResetPasswordPageProps = {
  token?: string
}

export function ResetPasswordPage({ token }: ResetPasswordPageProps) {
  const effectiveToken = token ?? getSearchToken()
  const setSession = useSession((state) => state.setSession)
  const resetMutation = useResetPasswordMutation()
  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm<ResetForm>({
    resolver: zodResolver(ResetFormSchema),
    defaultValues: { password: '', confirmPassword: '' },
  })
  const password = useWatch({ control, name: 'password' }) ?? ''

  const onSubmit = handleSubmit(async (values) => {
    if (!effectiveToken) {
      toast.error({
        title: 'Link de redefinição inválido',
        description: 'Solicite um novo link para criar sua senha.',
      })
      return
    }

    try {
      const response = await resetMutation.mutateAsync({
        token: effectiveToken,
        password: values.password,
      })
      if (response) {
        setSession(response.token, response.user)
      }
      toast.success('Senha redefinida.')
      navigateTo(response ? '/onboarding' : '/login')
    } catch (error) {
      const hasFieldErrors = applyApiFieldErrors(error, (field, fieldError) =>
        setError(field as Path<ResetForm>, fieldError),
      )
      if (hasFieldErrors) {
        toast.error({
          title: 'Revise os campos',
          description: 'Confira a nova senha e a confirmação.',
        })
        return
      }

      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível redefinir a senha',
          fallback: 'Solicite um novo link e tente novamente.',
        }),
      )
    }
  })

  return (
    <AuthLayout title="Nova senha" subtitle="Escolha uma nova senha para sua conta.">
      <form className={form} onSubmit={(event) => void onSubmit(event)}>
        <PasswordField
          label="Nova senha"
          autoComplete="new-password"
          error={errors.password?.message}
          strength={getPasswordStrength(password)}
          {...register('password')}
        />
        <PasswordField
          label="Confirmar senha"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />
        <div className={actions}>
          <Button type="submit" fullWidth disabled={resetMutation.isPending}>
            {resetMutation.isPending ? 'Salvando...' : 'Salvar senha'}
          </Button>
          <a className={link} href="/login">
            Voltar para login
          </a>
        </div>
      </form>
    </AuthLayout>
  )
}
